import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { SABOTEURS } from '@continuum/show-core';
import type { Room } from '../room/useRoom.js';
import { PRIORITY, useBanner } from '../ui/OverlayHost.js';
import { TopBanner } from '../ui/TopBanner.js';
import type { Lang } from '../ui/text.js';
import { postName } from '../ui/screenText.js';
import { CHAT_COPY, names } from './chatText.js';
import { Badge, ChatPanel } from './ChatPanel.js';
import type { Outgoing } from './store.js';
import { threadOf, unreadTotal } from './threads.js';
import type { ThreadKey } from './threads.js';

/**
 * The messages button in the header, with how many are new, and the messages behind it.
 *
 * Private messages, one guest to another, the whole table's conversation, and — on a saboteur's
 * phone only — the saboteurs' channel. The badge counts them all alike, so nothing on the outside of
 * the phone tells anyone at the table which kind came in. A new one gives a short buzz where the
 * phone can, and a notice at the top with who wrote — never what, and the same for every kind
 * (Marek, 2026-10-24) — that opens the conversation. While a game is open (`quiet`) the notice waits:
 * nothing interrupts the games.
 *
 * Alarms and meetings come first: the messages close when a meeting opens or this phone is
 * handed FIX, and whatever was being written waits in its box for later.
 */
export function ChatCorner({
  room,
  seat,
  lang,
  enabled,
  quiet = false,
}: {
  room: Room;
  seat: number;
  lang: Lang;
  enabled: boolean;
  /** A game is open on this phone: the notice waits until it closes. */
  quiet?: boolean;
}) {
  const c = CHAT_COPY[lang];
  const store = room.chat;
  const snap = useSyncExternalStore(store.subscribe, store.snapshot);
  const [open, setOpen] = useState(false);
  const [thread, setThread] = useState<ThreadKey | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  // Who wrote since the guest last looked, and the conversation the notice opens: the latest one's.
  const [notice, setNotice] = useState<{ key: ThreadKey; from: number[] } | null>(null);
  const saboteurs = snap.saboteurs !== null;
  const unread = unreadTotal(snap.messages, seat, snap.read, saboteurs);

  // A new evening: nothing written for the last one is left in a box.
  useEffect(() => {
    setDrafts({});
    setThread(null);
    setNotice(null);
  }, [snap.evening]);
  // The saboteurs were drawn again and this phone is not one now: the channel is gone.
  useEffect(() => {
    if (thread === SABOTEURS && !saboteurs) setThread(null);
  }, [thread, saboteurs]);
  useEffect(() => {
    if (!enabled) setOpen(false);
  }, [enabled]);

  const game = room.state?.game;
  const meeting = game?.meeting?.id ?? null;
  const alarm = game?.anomalies.find((a) => a.stabilisedAt === null && a.fracturedAt === null) ?? null;
  const fix = alarm && alarm.called.includes(seat) ? alarm.id : null;
  useEffect(() => {
    if (!meeting && !fix) return;
    setOpen(false);
    // The keyboard goes too, so the meeting or the alarm has the whole screen.
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
  }, [meeting, fix]);

  // A short buzz and a notice for a new message, unless the guest is already looking at that
  // conversation, or at the list of them.
  const arrived = snap.arrived;
  useEffect(() => {
    if (!arrived || arrived.from === seat) return;
    const key = threadOf(arrived, seat);
    if (open && (thread === null || thread === key) && document.visibilityState === 'visible') return;
    setNotice((was) => ({ key, from: [...(was?.from ?? []).filter((x) => x !== arrived.from), arrived.from] }));
    if (quiet) return;
    try {
      navigator.vibrate?.([30, 60, 30]);
    } catch {
      /* not on this phone (an iPhone has no web vibration): the notice and the badge say it */
    }
    // Only a new arrival buzzes, never a re-render.
  }, [arrived?.n]);
  // Looking at the conversation, or the list, is reading the notice.
  useEffect(() => {
    if (open && (thread === null || thread === notice?.key)) setNotice(null);
  }, [open, thread, notice?.key]);
  // Gone with the saboteurs' channel, should it go before the notice is read.
  useEffect(() => {
    if (notice?.key === SABOTEURS && !saboteurs) setNotice(null);
  }, [notice?.key, saboteurs]);

  const onRead = useCallback((key: ThreadKey) => store.read(key), [store]);

  const seats = room.state?.seats ?? [];
  const crew = seats.filter((s) => s.callsign && s.profession).map((s) => s.seat);
  const nameOf = (n: number) => seats.find((s) => s.seat === n)?.callsign ?? c.seat(n);

  // The same notice for every kind of message: who, never what, so a glance across the table
  // tells nobody who the saboteurs are.
  const shown = notice && enabled && !quiet ? notice : null;
  useBanner(
    'chat-notice',
    PRIORITY.chat,
    shown ? (
      <TopBanner
        compact
        tone={{ fg: '#e6edf3', bg: '#101820', border: 'rgba(79,216,194,.55)' }}
        autoHideMs={8_000}
        onDismiss={() => setNotice(null)}
        onOpen={() => {
          setThread(shown.key);
          setOpen(true);
          setNotice(null);
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingBottom: 2 }}>
          <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="#4fd8c2" strokeWidth="1.8" strokeLinejoin="round" style={{ flex: 'none' }}>
            <path d="M4 5.5h16a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1h-9l-4.5 3.5v-3.5H4a1 1 0 0 1-1-1v-9a1 1 0 0 1 1-1Z" />
          </svg>
          <span style={{ minWidth: 0 }}>
            <span style={{ display: 'block', fontFamily: "'Rajdhani', sans-serif", fontSize: 18, fontWeight: 700, letterSpacing: 1.2, lineHeight: 1.15 }}>
              {c.notice(names(shown.from.map(nameOf), lang).toUpperCase(), shown.from.length > 1)}
            </span>
            <span style={{ display: 'block', fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, color: '#9db3c2' }}>{c.noticeTap}</span>
          </span>
        </div>
      </TopBanner>
    ) : null,
  );

  if (!enabled) return null;

  const postOf = (n: number) => postName(seats.find((s) => s.seat === n)?.profession, lang);
  const key = thread === null ? '' : String(thread);
  const draft = drafts[key] ?? '';

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        aria-label={c.button(unread)}
        style={{
          position: 'relative',
          flex: 'none',
          width: 44,
          height: 44,
          borderRadius: 22,
          border: '1px solid rgba(255,255,255,.22)',
          background: 'rgba(255,255,255,.06)',
          color: 'inherit',
          display: 'grid',
          placeItems: 'center',
          padding: 0,
        }}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round">
          <path d="M4 5.5h16a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1h-9l-4.5 3.5v-3.5H4a1 1 0 0 1-1-1v-9a1 1 0 0 1 1-1Z" />
        </svg>
        {unread > 0 && (
          <span style={{ position: 'absolute', top: -4, right: -4 }}>
            <Badge count={unread} />
          </span>
        )}
      </button>
      {open && (
        <ChatPanel
          lang={lang}
          seat={seat}
          snap={snap}
          crew={crew}
          nameOf={nameOf}
          postOf={postOf}
          thread={thread}
          draft={draft}
          onThread={setThread}
          onDraft={(text) => setDrafts((d) => ({ ...d, [key]: text }))}
          onSend={() => {
            if (thread === null || !draft.trim()) return;
            store.send(thread, draft);
            setDrafts((d) => ({ ...d, [key]: '' }));
          }}
          onRetry={(message: Outgoing) => {
            store.discard(message.id);
            store.send(message.to, message.text);
          }}
          onRead={onRead}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}
